import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Plus, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  VehicleBookingDialog,
  bookingLabel,
  formatBookingDates,
  formatBookingDrivers,
} from "@/components/vehicles/VehicleBookingDialog";
import {
  bookingCategoryLabels,
  useVehicleBookings,
  type VehicleBooking,
} from "@/hooks/useVehicles";
import { cn } from "@/lib/utils";
import { splitUpcomingAndPastBookings } from "@/lib/vehicle-bookings";
import { todayZeroDate } from "@/lib/vehicle-deadlines";

function BookingRow({
  booking,
  onEdit,
}: {
  booking: VehicleBooking;
  onEdit: () => void;
}) {
  const isProject = booking.category === "project";
  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-muted/50"
      onClick={onEdit}
      data-testid={`vehicle-booking-${booking.id}`}
    >
      <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium">
            {isProject && booking.projectID ? (
              <Link
                to={`/projects/${booking.projectID}/vehicles`}
                className="hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                {bookingLabel(booking)}
              </Link>
            ) : (
              bookingLabel(booking)
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatBookingDates(booking)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isProject ? "secondary" : "outline"}>
            {bookingCategoryLabels[booking.category]}
          </Badge>
          {formatBookingDrivers(booking) && (
            <Badge variant="outline" className="gap-1">
              <UserRound className="h-3 w-3" />
              {formatBookingDrivers(booking)}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Where the vehicle is booked: upcoming on top, past collapsed. */
export function VehicleBookingsTab({ vehicleId }: { vehicleId: string }) {
  const { data: bookings, isLoading } = useVehicleBookings(vehicleId);
  const [showPast, setShowPast] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    booking: VehicleBooking | null;
  }>({ open: false, booking: null });
  const defaults = useMemo(() => ({ vehicleID: vehicleId }), [vehicleId]);

  const { upcoming, past } = useMemo(
    () => splitUpcomingAndPastBookings(bookings, todayZeroDate()),
    [bookings],
  );

  const edit = (booking: VehicleBooking) => setDialog({ open: true, booking });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3
          className="text-sm font-semibold text-muted-foreground"
          data-testid="vehicle-bookings-upcoming-title"
        >
          Upcoming bookings ({upcoming.length})
        </h3>
        <Button
          offlineEdit
          onClick={() => setDialog({ open: true, booking: null })}
          data-testid="new-vehicle-booking"
        >
          <Plus className="h-4 w-4 mr-2" />
          New booking
        </Button>
      </div>

      {isLoading ? (
        <p className="py-4 text-center text-muted-foreground">Loading...</p>
      ) : upcoming.length === 0 ? (
        <p className="text-sm text-muted-foreground">No upcoming bookings.</p>
      ) : (
        <div className="space-y-2">
          {upcoming.map((booking) => (
            <BookingRow
              key={booking.id}
              booking={booking}
              onEdit={() => edit(booking)}
            />
          ))}
        </div>
      )}

      {past.length > 0 && (
        <Collapsible open={showPast} onOpenChange={setShowPast}>
          <CollapsibleTrigger
            className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
            data-testid="vehicle-bookings-past-toggle"
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                showPast && "rotate-90",
              )}
            />
            Past bookings ({past.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {past.map((booking) => (
              <BookingRow
                key={booking.id}
                booking={booking}
                onEdit={() => edit(booking)}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      <VehicleBookingDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        booking={dialog.booking}
        defaults={defaults}
      />
    </div>
  );
}
